'use client';

/* ============================================================================
 * LandingV2 - prostaysites.com homepage.
 * ============================================================================
 * High-conversion landing for Instant Site. Section order:
 *   1  Hero          - headline + live editor scene + stats
 *   2  How it works  - three steps
 *   3  Templates     - six-template teaser with CSS previews
 *   4  Live data     - the PMS-fed USP, split layout
 *   5  SEO proof     - static pages + structured data
 *   6  Pricing tease - included with the booking engine
 *   7  FAQ           - six questions with FAQPage schema upstream
 *   8  Closing       - demo CTA
 * All copy lives in messages/<locale>.json under Pages.Landing.
 * ========================================================================= */

import React, {useState} from 'react';
import {useTranslations} from 'next-intl';
import {Link} from '@/i18n/navigation';
import {Reveal, RevealStagger, RevealLine} from '@/components/v2/Reveal';
import InstantSiteScene from '@/components/v2/InstantSiteScene';
import TemplateShowcase from '@/components/v2/templateShowcase/TemplateShowcase';

const NS = 'Pages.Landing';
const TOTAL = 8;

const TEMPLATES = [
  {name: 'Classic',    slug: 'classic'},
  {name: 'Modern',     slug: 'modern'},
  {name: 'Editorial',  slug: 'editorial'},
  {name: 'Minimal',    slug: 'minimal'},
  {name: 'Resort',     slug: 'resort'},
  {name: 'Futuristic', slug: 'futuristic'}
];

function Container({children, className = ''}) {
  return <div className={`container-page ${className}`}>{children}</div>;
}

function SectionAnchor({id, label}) {
  return (
    <Reveal className="mb-10 flex items-center gap-3 text-foreground-subtle">
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {String(id).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
      </span>
      <RevealLine className="block h-px flex-1 bg-border" delay={0.1} />
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">{label}</span>
    </Reveal>
  );
}

function CheckIcon({className = ''}) {
  return (
    <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M11.5 4L5.5 10L2.5 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* 1 - Hero ------------------------------------------------------------------ */

function SectionHero() {
  const t = useTranslations(NS);
  const stats = t.raw('hero.stats');
  return (
    <section className="bg-hero-mesh relative overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32">
      <Container>
        <SectionAnchor id={1} label={t('anchors.hero')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-6">
            <Reveal eager>
              <p className="fig-label">{t('hero.eyebrow')}</p>
            </Reveal>
            <Reveal delay={0.06} eager>
              <h1 className="headline-hero mt-5 max-w-[16ch] text-foreground">
                {t('hero.headline')}
              </h1>
            </Reveal>
            <Reveal delay={0.12} eager>
              <p className="lead mt-6 max-w-[46ch]">{t('hero.lead')}</p>
            </Reveal>
            <Reveal delay={0.18} eager>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link href="/request-demo/" className="cta-pill-brand">
                  {t('hero.primaryCta')} <span aria-hidden>&rarr;</span>
                </Link>
                <Link href="/templates/" className="cta-text">
                  {t('hero.secondaryCta')} <span aria-hidden>&rarr;</span>
                </Link>
              </div>
            </Reveal>
            <RevealStagger
              as="dl"
              mode="clone"
              className="mt-14 grid grid-cols-3 gap-6 border-t border-border pt-6"
              gap={0.06}
              initialDelay={0.24}
              eager
            >
              {stats.map((stat) => (
                <div key={stat.label}>
                  <dd className="text-2xl font-medium tracking-tight text-foreground md:text-3xl">
                    {stat.value}
                  </dd>
                  <dt className="mt-1 font-mono text-[10.5px] tracking-[0.14em] uppercase text-foreground-subtle">
                    {stat.label}
                  </dt>
                </div>
              ))}
            </RevealStagger>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.2} eager className="relative">
              <div className="glow-brand-soft" />
              <InstantSiteScene />
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* 2 - How it works ------------------------------------------------------------ */

function SectionHow() {
  const t = useTranslations(NS);
  const steps = t.raw('how.steps');
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={2} label={t('anchors.how')} />
        <div className="mb-14 grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-section max-w-[22ch] text-foreground">
                {t('how.headline')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.06}>
              <p className="lead">{t('how.lead')}</p>
            </Reveal>
          </div>
        </div>
        <RevealStagger
          as="ol"
          mode="clone"
          className="grid grid-cols-1 divide-y divide-border border-y border-border md:grid-cols-3 md:divide-y-0 md:divide-x"
          gap={0.06}
        >
          {steps.map((step, i) => (
            <li key={step.title} className="p-7 md:p-8">
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
                {String(i + 1).padStart(2, '0')}
              </p>
              <h3 className="mt-3 text-xl font-medium tracking-tight text-foreground">
                {step.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-foreground-muted">
                {step.body}
              </p>
            </li>
          ))}
        </RevealStagger>
      </Container>
    </section>
  );
}

/* 3 - Templates teaser --------------------------------------------------------- */

function SectionTemplates() {
  const t = useTranslations(NS);
  return (
    <section className="section-rhythm bg-background">
      <Container>
        <SectionAnchor id={3} label={t('anchors.templates')} />
        <div className="mb-14 grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal>
              <p className="fig-label">{t('templates.eyebrow')}</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="headline-section mt-4 max-w-[22ch] text-foreground">
                {t('templates.headline')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:self-end">
            <Reveal delay={0.1}>
              <p className="lead">{t('templates.lead')}</p>
            </Reveal>
          </div>
        </div>
        <RevealStagger
          as="ul"
          mode="clone"
          className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
          gap={0.05}
        >
          {TEMPLATES.map(({name, slug}) => (
            <li key={slug} className="surface-elevated group overflow-hidden p-4 transition-shadow hover:shadow-lg">
              <Link href={`/templates/${slug}/`} aria-label={name} className="block">
                <TemplateShowcase slug={slug} />
                <p className="mt-4 flex items-baseline justify-between px-1 text-[15px] font-medium tracking-tight text-foreground">
                  {name}
                  <span className="cta-text-brand text-[12px] opacity-0 transition-opacity group-hover:opacity-100">
                    <span aria-hidden>&rarr;</span>
                  </span>
                </p>
              </Link>
            </li>
          ))}
        </RevealStagger>
        <Reveal delay={0.1}>
          <div className="mt-12">
            <Link href="/templates/" className="cta-text-brand">
              {t('templates.cta')} <span aria-hidden>&rarr;</span>
            </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

/* 4 - Live data USP ------------------------------------------------------------ */

function SectionLiveData() {
  const t = useTranslations(NS);
  const bullets = t.raw('liveData.bullets');
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={4} label={t('anchors.liveData')} />
        <div className="grid grid-cols-1 items-start gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <p className="fig-label">{t('liveData.eyebrow')}</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="headline-section mt-4 max-w-[18ch] text-foreground">
                {t('liveData.headline')}
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="lead mt-6">{t('liveData.body')}</p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.1}>
              <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
                {t('liveData.bulletsLabel')}
              </p>
              <ul className="mt-6 space-y-5 border-t border-border pt-6">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] leading-relaxed text-foreground-muted">
                    <CheckIcon className="mt-1.5 size-3.5 flex-shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* 5 - SEO proof ------------------------------------------------------------------ */

function SectionSeo() {
  const t = useTranslations(NS);
  const bullets = t.raw('seo.bullets');
  return (
    <section className="section-rhythm bg-background">
      <Container>
        <SectionAnchor id={5} label={t('anchors.seo')} />
        <div className="grid grid-cols-1 items-start gap-12 md:grid-cols-12 md:gap-10">
          <div className="order-2 md:order-1 md:col-span-7">
            <Reveal delay={0.1} className="relative">
              <div className="surface-elevated overflow-hidden p-6 font-mono text-[12px] leading-relaxed text-foreground-muted md:p-8">
                <p className="text-foreground-subtle">&lt;!-- every publish emits --&gt;</p>
                <p className="mt-2">&lt;script type=&quot;application/ld+json&quot;&gt;</p>
                <p className="pl-4">&#123;&quot;@type&quot;: &quot;<span className="text-primary">Hotel</span>&quot;,</p>
                <p className="pl-5">&quot;name&quot;: &quot;Casa Miramar&quot;,</p>
                <p className="pl-5">&quot;containsPlace&quot;: [&#123;&quot;@type&quot;: &quot;<span className="text-primary">HotelRoom</span>&quot;...</p>
                <p>&lt;/script&gt;</p>
                <p className="mt-3 text-foreground-subtle">&lt;!-- plus --&gt;</p>
                <p className="mt-1">sitemap.xml &middot; robots.txt &middot; hreflang</p>
              </div>
            </Reveal>
          </div>
          <div className="order-1 md:order-2 md:col-span-5">
            <Reveal>
              <p className="fig-label">{t('seo.eyebrow')}</p>
            </Reveal>
            <Reveal delay={0.06}>
              <h2 className="headline-section mt-4 max-w-[18ch] text-foreground">
                {t('seo.headline')}
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="lead mt-6">{t('seo.body')}</p>
            </Reveal>
            <Reveal delay={0.16}>
              <ul className="mt-8 space-y-3">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[14px] leading-relaxed text-foreground-muted">
                    <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* 6 - Pricing teaser ------------------------------------------------------------- */

function SectionPricingTeaser() {
  const t = useTranslations(NS);
  return (
    <section className="section-rhythm">
      <Container>
        <SectionAnchor id={6} label={t('anchors.pricing')} />
        <div className="surface-elevated relative overflow-hidden p-8 md:p-14">
          <div className="glow-brand-soft" />
          <div className="relative grid grid-cols-1 items-center gap-8 md:grid-cols-12">
            <div className="md:col-span-8">
              <Reveal>
                <h2 className="headline-section max-w-[24ch] text-foreground">
                  {t('pricingTeaser.headline')}
                </h2>
              </Reveal>
              <Reveal delay={0.06}>
                <p className="lead mt-5 max-w-[56ch]">{t('pricingTeaser.body')}</p>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Reveal delay={0.1}>
                <Link href="/pricing/" className="cta-pill-brand">
                  {t('pricingTeaser.cta')} <span aria-hidden>&rarr;</span>
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* 7 - FAQ ---------------------------------------------------------------------- */

function FaqItem({item, open, onToggle}) {
  return (
    <li className="border-b border-border">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-6 py-6 text-left"
      >
        <span className="text-[16px] font-medium tracking-tight text-foreground md:text-lg">
          {item.q}
        </span>
        <span
          aria-hidden
          className={`mt-1 flex size-6 flex-shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-transform duration-200 ${
            open ? 'rotate-45' : ''
          }`}
        >
          <svg viewBox="0 0 12 12" className="size-3" fill="none">
            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="max-w-[70ch] pb-6 text-[14.5px] leading-relaxed text-foreground-muted">
            {item.a}
          </p>
        </div>
      </div>
    </li>
  );
}

function SectionFaq() {
  const t = useTranslations(NS);
  const items = t.raw('faq.items');
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="section-rhythm bg-background">
      <Container>
        <SectionAnchor id={7} label={t('anchors.faq')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <h2 className="headline-section max-w-[14ch] text-foreground">
                {t('faq.headline')}
              </h2>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="lead mt-5">{t('faq.lead')}</p>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.08}>
              <ul className="border-t border-border">
                {items.map((item, i) => (
                  <FaqItem
                    key={item.q}
                    item={item}
                    open={openIndex === i}
                    onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                  />
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* 8 - Closing -------------------------------------------------------------------- */

function SectionClosing() {
  const t = useTranslations(NS);
  const benefits = t.raw('closing.benefits');
  return (
    <section className="section-rhythm-lg">
      <Container>
        <SectionAnchor id={8} label={t('anchors.closing')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="headline-manifesto max-w-[18ch] text-foreground">
                {t('closing.headline')}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="lead mt-6 max-w-[48ch]">{t('closing.body')}</p>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link href="/request-demo/" className="cta-pill-brand">
                  {t('closing.primaryCta')} <span aria-hidden>&rarr;</span>
                </Link>
                <Link href="/features/" className="cta-text">
                  {t('closing.secondaryCta')} <span aria-hidden>&rarr;</span>
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <RevealStagger
              as="ul"
              mode="clone"
              className="space-y-4 border-t border-border pt-6 md:mt-4"
              gap={0.06}
            >
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] leading-relaxed text-foreground-muted">
                  <CheckIcon className="mt-1.5 size-3.5 flex-shrink-0 text-primary" />
                  <span>{b}</span>
                </li>
              ))}
            </RevealStagger>
          </div>
        </div>
      </Container>
    </section>
  );
}

export default function LandingV2() {
  return (
    <>
      <SectionHero />
      <SectionHow />
      <SectionTemplates />
      <SectionLiveData />
      <SectionSeo />
      <SectionPricingTeaser />
      <SectionFaq />
      <SectionClosing />
    </>
  );
}
